import { Box, Typography, Tooltip } from '@mui/material';

interface AlertLineProps {
  position: number; // Percentage position (0-1)
  distanceFeet: number;
  distanceMeters: number;
  displayDistance: number;
  unit: 'feet' | 'meters';
  label: string;
  image: string;
  color: string;
  alignment: 'left' | 'right';
  lineStyle: 'dashed' | 'solid';
  exceedsMax?: boolean;
}

export default function AlertLine({
  position,
  distanceFeet,
  distanceMeters,
  displayDistance,
  unit,
  label,
  image,
  color,
  alignment,
  lineStyle,
  exceedsMax = false,
}: AlertLineProps) {
  const isLeft = alignment === 'left';
  const tooltipLabel = exceedsMax
    ? `${label}: >${distanceFeet}ft (>${distanceMeters}m)`
    : `${label}: ${distanceFeet}ft (${distanceMeters}m)`;

  return (
    <>
      {/* Alert Line */}
      <Tooltip title={tooltipLabel}>
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: `${position * 100}%`,
            height: 140,
            ...(lineStyle === 'dashed'
              ? {
                  borderLeft: '2px dashed',
                  borderColor: color,
                }
              : {
                  width: 2,
                  backgroundColor: color,
                  borderRadius: 1,
                }),
          }}
        />
      </Tooltip>

      {/* Container for Image + Label (stacked vertically) */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 150,
          left: isLeft
            ? `calc(${position * 100}% - 120px)`
            : `calc(${position * 100}% + 0px)`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isLeft ? 'flex-end' : 'flex-start',
          gap: 0.5,
        }}
      >
        {/* Image */}
        <Box
          component="img"
          src={image}
          alt={label}
          sx={{
            width: 120,
            height: 'auto',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.35)',
          }}
        />

        {/* Label */}
        <Typography
          variant="caption"
          sx={{
            color: color,
            fontWeight: 600,
            textAlign: isLeft ? 'right' : 'left',
            maxWidth: '120px',
            lineHeight: 1.2,
          }}
        >
          {label} ({exceedsMax ? '>' : ''}
          {displayDistance}
          {unit === 'feet' ? 'ft' : 'm'})
        </Typography>
      </Box>
    </>
  );
}
